import { useQuery } from '@tanstack/react-query'
import {
  Alert, Box, Skeleton, Stack, Typography, useMediaQuery, useTheme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { fetchTrialBalance } from '../api/endpoints'
import ReportShell, { rangeLabel } from './ReportShell'
import ReportFooter from './ReportFooter'
import ReportToolbar from './ReportToolbar'
import { useDateRange } from './useDateRange'
import { downloadCsv } from './csv'
import { c, fmtMoney } from '../theme'

export default function TrialBalance({ onLedger }: { onLedger?: (c: string) => void }) {
  const { t, i18n } = useTranslation()
  const { range, setRange, presets } = useDateRange()
  // Four columns do not fit 390px. The table used to render anyway and
  // the Credit column — half the report — ran off the right edge with
  // nothing to indicate it was there.
  const isPhone = useMediaQuery(useTheme().breakpoints.down('md'))

  const q = useQuery({
    queryKey: ['trial-balance', range.from, range.to],
    queryFn: () => fetchTrialBalance({ from_date: range.from, to_date: range.to }),
  })
  const tb = q.data

  return (
    <ReportShell titleKey="nav.trialBalance"
      period={rangeLabel(range.from, range.to, i18n.language, t)}
      toolbar={
        <ReportToolbar range={range} setRange={setRange} presets={presets}
        pdf={{ path: 'trial-balance.pdf',
               params: { from_date: range.from, to_date: range.to } }}
          onExport={tb ? () => downloadCsv(`trial-balance-${range.to}`, [
            ['Code', 'Account', 'Opening', 'Debit', 'Credit', 'Closing Dr', 'Closing Cr'],
            ...tb.rows.map((r) => [r.code, r.name_en, r.opening, r.debit,
                                   r.credit, r.closing_dr, r.closing_cr]),
            ['', 'Total', '', '', '', tb.total_debit, tb.total_credit],
          ]) : undefined} />
      }>
      {q.isLoading ? (
        <Box sx={{ p: 2 }}><Skeleton /><Skeleton /><Skeleton /></Box>
      ) : !tb?.rows.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
          {t('report.nothingInRange')}
        </Typography>
      ) : (
        <Box sx={{ px: isPhone ? 0 : 2, pb: 1 }}>
          {isPhone ? (
            /* One row per account, the balance on its own line with the
               side named. Dr/Cr as a word beats a column the reader
               cannot see. */
            <Box className="ruled">
              {tb.rows.map((r) => {
                const dr = Number(r.closing_dr)
                const cr = Number(r.closing_cr)
                return (
                  <Stack key={r.code} direction="row" spacing={2}
                    alignItems="center" justifyContent="space-between"
                    onClick={() => onLedger?.(r.code)}
                    sx={{ px: 2, py: 1.1,
                          cursor: onLedger ? 'pointer' : 'default',
                          '&:active': { bgcolor: c.primarySoft } }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5 }}>{r.name_en}</Typography>
                      <span className="num" style={{ fontSize: 11,
                        color: c.inkMuted }}>{r.code}</span>
                    </Box>
                    <Stack direction="row" spacing={0.75} alignItems="baseline"
                      sx={{ flexShrink: 0 }}>
                      <span className="num" style={{ fontSize: 13.5 }}>
                        {fmtMoney(dr ? r.closing_dr : r.closing_cr)}
                      </span>
                      <span className="side" style={{
                        color: c.inkMuted }}>
                        {dr ? t('entry.dr') : cr ? t('entry.cr') : ''}
                      </span>
                    </Stack>
                  </Stack>
                )
              })}
              <Stack direction="row" justifyContent="space-between"
                sx={{ px: 2, py: 1.2, borderTop: `1px solid ${c.border}`,
                      bgcolor: c.surfacePage }}>
                <span className="section-label">{t('report.total')}</span>
                <Stack direction="row" spacing={2}>
                  <span className="num" style={{ fontWeight: 600 }}>
                    {fmtMoney(tb.total_debit)}
                  </span>
                  <span className="num" style={{ fontWeight: 600 }}>
                    {fmtMoney(tb.total_credit)}
                  </span>
                </Stack>
              </Stack>
            </Box>
          ) : (
          <table className="rpt">
            <thead>
              <tr>
                <th style={{ width: 90 }}>{t('entry.code')}</th>
                <th>{t('report.account')}</th>
                <th className="num" style={{ width: 120 }}>{t('entry.debit')}</th>
                <th className="num" style={{ width: 120 }}>{t('entry.credit')}</th>
              </tr>
            </thead>
            <tbody>
              {tb.rows.map((r) => (
                <tr key={r.code} className={onLedger ? 'clickable' : undefined}
                  onClick={() => onLedger?.(r.code)}>
                  <td className="num" style={{ fontSize: '0.78rem',
                    color: 'var(--ink-muted)', textAlign: 'start' }}>{r.code}</td>
                  <td>{r.name_en}</td>
                  <td className="num">
                    {Number(r.closing_dr) ? fmtMoney(r.closing_dr) : ''}</td>
                  <td className="num">
                    {Number(r.closing_cr) ? fmtMoney(r.closing_cr) : ''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td colSpan={2}>{t('report.total')}</td>
                <td className="num">{fmtMoney(tb.total_debit)}</td>
                <td className="num">{fmtMoney(tb.total_credit)}</td>
              </tr>
            </tfoot>
          </table>
          )}

          {!tb.is_balanced && (
            <Alert severity="error" sx={{ mt: 2, mx: isPhone ? 2 : 0 }}>
              {t('report.outOfBalance')}</Alert>
          )}
        </Box>
      )}
      <ReportFooter />
    </ReportShell>
  )
}
